import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Zap, Clock, Mail, MessageCircle, Trash2, X, CalendarClock } from 'lucide-react';
import {
  getJourneySteps,
  createJourneyStep,
  updateJourneyStep,
  getEmailTemplates,
  getSmsTemplates,
} from '../../../data/api';
import { Loading, ErrorFallback, useToast } from './CiShared';
import './automationsScreen.css';

// Automations — a real workflow builder backed by journey_steps.
//
// Each automation IS a journey step: a trigger (who/when it applies to) +
// a SCHEDULE + an action (a template on a channel). The enable/disable toggle
// writes status to the API (active|paused) so it persists — it is never just
// visual. Create Automation -> createJourneyStep with the chosen config.
//
// SCHEDULE — two ways, with an absolute date+time as the PRIMARY one:
//   1. Absolute  → user picks a real calendar date and clock time via native
//      <input type="date"> + <input type="time">. Persisted as
//      timing_mode = 'fixed_datetime' with fixed_at = "YYYY-MM-DDTHH:MM".
//   2. Relative (optional) → "N units before/after the event". Persisted as
//      timing_mode = 'offset' with offset_value (signed; negative = before)
//      and offset_unit. This keeps the old behaviour available without making
//      the confusing "N days after trigger" the default.
//
// We persist the trigger choice inside step_code on create (stable slug) and
// surface it in the row copy so the workflow reads end-to-end.

const TRIGGERS = [
  { key: 'guest_invited', label: 'Guest is invited', anchor: 'invitation' },
  { key: 'no_rsvp', label: "Guest hasn't RSVP'd", anchor: 'invitation' },
  { key: 'rsvp_yes', label: 'Guest RSVPs Yes', anchor: 'rsvp' },
  { key: 'before_event', label: 'Before the event', anchor: 'event' },
  { key: 'after_event', label: 'After the event', anchor: 'event' },
];

// Relative-mode direction (only used when the user opts into "relative to event").
const RELATIVE_DIRS = [
  { key: 'before_event', label: 'before the event' },
  { key: 'after_event', label: 'after the event' },
];

const OFFSET_UNITS = ['minutes', 'hours', 'days', 'weeks'];

const ENABLED = 'active';
const DISABLED = 'paused';
const isEnabled = (status) => String(status || '').toLowerCase() === ENABLED;

function triggerFromStep(step) {
  // step_code is "<trigger_key>__<slug>" when created here; fall back to anchor heuristics.
  const code = String(step.step_code || '');
  const fromCode = TRIGGERS.find((t) => code.startsWith(`${t.key}__`) || code === t.key);
  if (fromCode) return fromCode;
  if ((step.offset_value ?? 0) < 0) return TRIGGERS.find((t) => t.key === 'before_event');
  return TRIGGERS[0];
}

// Format a stored fixed_at ("YYYY-MM-DDTHH:MM" or ISO) for display.
function formatFixed(value) {
  let when = String(value || '');
  try {
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) {
      when = dt.toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    }
  } catch { /* keep raw */ }
  return when;
}

// Human-readable timing line for a saved step.
function timingLabel(step) {
  if (step.timing_mode === 'fixed_datetime' && step.fixed_at) {
    return `${formatFixed(step.fixed_at)}`;
  }
  const v = Number(step.offset_value ?? 0);
  const unit = step.offset_unit || 'days';
  if (v < 0) return `${Math.abs(v)} ${unit} before the event`;
  return v === 0 ? 'At the event start' : `${v} ${unit} after the event`;
}

// A sensible default date for new absolute automations: tomorrow at 10:00.
function defaultFixed() {
  const dt = new Date();
  dt.setDate(dt.getDate() + 1);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
    time: '10:00',
  };
}

const blankDraft = () => {
  const f = defaultFixed();
  return {
    name: '',
    trigger: 'guest_invited',
    // Absolute date+time is the PRIMARY scheduling mode.
    mode: 'absolute',          // 'absolute' | 'relative'
    fixedDate: f.date,         // YYYY-MM-DD  (native <input type="date">)
    fixedTime: f.time,         // HH:MM       (native <input type="time">)
    // Relative-to-event mode (optional, secondary).
    offsetValue: 7,
    offsetUnit: 'days',
    relativeDir: 'before_event',
    // Action.
    channel: 'email',
    templateId: '',
  };
};

export default function AutomationsScreen() {
  const [steps, setSteps] = useState(null);
  const [err, setErr] = useState(false);
  const [emailTpls, setEmailTpls] = useState([]);
  const [smsTpls, setSmsTpls] = useState([]);
  const [busyId, setBusyId] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | step.id
  const [draft, setDraft] = useState(blankDraft());
  const [saving, setSaving] = useState(false);
  const { show, node: toastNode } = useToast();

  const load = () => {
    setErr(false);
    getJourneySteps()
      .then((d) => setSteps(Array.isArray(d) ? d : []))
      .catch(() => { setErr(true); setSteps([]); });
  };

  useEffect(() => {
    load();
    getEmailTemplates().then((d) => setEmailTpls(Array.isArray(d) ? d : [])).catch(() => setEmailTpls([]));
    getSmsTemplates().then((d) => setSmsTpls(Array.isArray(d) ? d : [])).catch(() => setSmsTpls([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tplOptions = draft.channel === 'sms' ? smsTpls : emailTpls;
  const tplName = useMemo(() => {
    const map = {};
    [...emailTpls, ...smsTpls].forEach((t) => { map[String(t.id || t._id)] = t.name; });
    return (id) => (id ? (map[String(id)] || 'Selected template') : null);
  }, [emailTpls, smsTpls]);

  // Build the journey_steps payload from a draft.
  const draftToPayload = (d) => {
    const base = {
      name: d.name.trim() || 'Untitled automation',
      channel: d.channel,
      template_id: d.templateId || null,
    };
    if (d.mode === 'absolute') {
      // Store the chosen calendar date + clock time in the step's fixed_at
      // field as a local "YYYY-MM-DDTHH:MM" string (what fixed_datetime steps
      // already use). Clear the relative fields so they can't conflict.
      return {
        ...base,
        timing_mode: 'fixed_datetime',
        fixed_at: joinLocal(d.fixedDate, d.fixedTime),
        offset_value: null,
        offset_unit: null,
      };
    }
    // Relative-to-event: sign the magnitude negative for "before".
    const magnitude = Math.abs(Number(d.offsetValue) || 0);
    const signed = d.relativeDir === 'before_event' ? -magnitude : magnitude;
    return { ...base, timing_mode: 'offset', fixed_at: null, offset_value: signed, offset_unit: d.offsetUnit || 'days' };
  };

  // An absolute schedule needs a real date before it can be saved.
  const draftValid = (d) => d.mode !== 'absolute' || !!d.fixedDate;

  const openNew = () => { setDraft(blankDraft()); setEditing('new'); };

  const openEdit = (step) => {
    const trig = triggerFromStep(step);
    const isAbsolute = step.timing_mode === 'fixed_datetime';
    const def = defaultFixed();
    const parts = isAbsolute && step.fixed_at ? splitLocal(step.fixed_at) : null;
    const offset = Number(step.offset_value ?? 0);
    setDraft({
      name: step.name || '',
      trigger: trig.key,
      mode: isAbsolute ? 'absolute' : 'relative',
      fixedDate: parts?.date || def.date,
      fixedTime: parts?.time || def.time,
      offsetValue: Math.abs(offset) || 7,
      offsetUnit: step.offset_unit || 'days',
      relativeDir: offset < 0 ? 'before_event' : 'after_event',
      channel: step.channel || 'email',
      templateId: step.template_id || '',
    });
    setEditing(step.id);
  };

  const closeEditor = () => { setEditing(null); setSaving(false); };

  const saveDraft = async () => {
    if (saving) return;
    if (!draftValid(draft)) {
      show('Pick a date for this automation first');
      return;
    }
    setSaving(true);
    const payload = draftToPayload(draft);
    try {
      if (editing === 'new') {
        // Encode the trigger into step_code so it survives + reads back.
        const slug = (draft.name.trim() || draft.trigger).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        await createJourneyStep({ ...payload, status: ENABLED, step_code: `${draft.trigger}__${slug}` });
        show('Automation created');
      } else {
        await updateJourneyStep(editing, payload);
        show('Automation updated');
      }
      closeEditor();
      load();
    } catch {
      show(editing === 'new' ? 'Could not create automation' : 'Could not save changes');
      setSaving(false);
    }
  };

  // Enable/disable — persists status via the API (NOT just visual).
  const toggle = async (step) => {
    if (busyId) return;
    setBusyId(step.id);
    const next = isEnabled(step.status) ? DISABLED : ENABLED;
    // optimistic
    setSteps((arr) => (arr || []).map((s) => (s.id === step.id ? { ...s, status: next } : s)));
    try {
      await updateJourneyStep(step.id, { status: next });
      show(next === ENABLED ? 'Automation turned on' : 'Automation paused');
    } catch {
      // revert on failure
      setSteps((arr) => (arr || []).map((s) => (s.id === step.id ? { ...s, status: step.status } : s)));
      show('Could not update — try again');
    } finally {
      setBusyId('');
    }
  };

  // Disable (soft delete) — there is no archive endpoint exposed here, so we
  // park the automation as paused rather than offering a dead "delete".
  const archive = async (step) => {
    if (busyId) return;
    setBusyId(step.id);
    try {
      await updateJourneyStep(step.id, { status: DISABLED });
      show('Automation paused');
      load();
    } catch {
      show('Could not pause this automation');
    } finally {
      setBusyId('');
    }
  };

  if (steps === null) return <div className="ci-view"><Loading label="Loading automations…" /></div>;

  return (
    <div className="ci-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <p style={{ fontSize: 13, color: 'var(--ci-txt2)', margin: 0 }}>Relationship-journey automation — build workflows that nurture every guest. Each automation is a step in your guest journey.</p>
        <button className="ci-btn ci-gold ci-sm" onClick={openNew}><Plus size={13} /> Create Automation</button>
      </div>

      {err && <ErrorFallback message="The automation workflows couldn't be reached." onRetry={load} />}

      {!err && steps.length === 0 && editing !== 'new' && (
        <div className="ci-glass" style={{ padding: 26 }}>
          <div className="ci-empty">No automations yet. Create your first workflow — a welcome series, an RSVP reminder, or a post-event thank-you.</div>
        </div>
      )}

      {!err && steps.map((step) => {
        const on = isEnabled(step.status);
        const Icon = step.channel === 'sms' ? MessageCircle : Mail;
        const trig = triggerFromStep(step);
        const tpl = tplName(step.template_id);
        return (
          <div className="ci-auto-row" key={step.id}>
            <div className="ci-auto-ic"><Icon size={18} /></div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div className="ci-an">{step.name || 'Untitled automation'}</div>
              <div className="ci-ad">
                <Zap size={11} style={{ verticalAlign: -1, marginRight: 4 }} />Trigger: {trig.label}
                {' · '}
                {tpl ? <>Sends <b style={{ color: 'var(--ci-txt2)' }}>{tpl}</b> via {step.channel === 'sms' ? 'SMS' : 'Email'}</> : <span style={{ color: 'var(--ci-gold-hi)' }}>No template assigned yet</span>}
              </div>
            </div>
            <span className={`ci-badge ${on ? 'ci-active' : 'ci-sched'}`}>{on ? 'Active' : 'Paused'}</span>
            <span className="ci-whenw"><Clock size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{timingLabel(step)}</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="ci-btn ci-ghost ci-sm" onClick={() => openEdit(step)}>Edit</button>
              <button
                className={`ci-toggle ${on ? '' : 'ci-off'}`}
                aria-label={`${on ? 'Pause' : 'Activate'} ${step.name}`}
                disabled={busyId === step.id}
                onClick={() => toggle(step)}
              />
            </div>
          </div>
        );
      })}

      {(editing === 'new' || (editing && steps.some((s) => s.id === editing))) && (
        <AutomationEditor
          mode={editing === 'new' ? 'new' : 'edit'}
          draft={draft}
          setDraft={setDraft}
          tplOptions={tplOptions}
          saving={saving}
          onSave={saveDraft}
          onCancel={closeEditor}
          onArchive={editing !== 'new' ? () => archive(steps.find((s) => s.id === editing)) : null}
          busy={!!busyId}
        />
      )}

      {toastNode}
    </div>
  );
}

// Break a stored fixed_at into { date: "YYYY-MM-DD", time: "HH:MM" } for the
// native <input type="date"> / <input type="time"> pair. Handles both the
// local "YYYY-MM-DDTHH:MM" form we write and full ISO timestamps.
function splitLocal(value) {
  const raw = String(value || '');
  // Fast path: already "YYYY-MM-DDTHH:MM[...]" — slice without timezone drift.
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (m) return { date: m[1], time: m[2] };
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return { date: '', time: '' };
    const pad = (n) => String(n).padStart(2, '0');
    return {
      date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
      time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
    };
  } catch {
    return { date: '', time: '' };
  }
}

// Recombine a date + time into the "YYYY-MM-DDTHH:MM" string stored in fixed_at.
// Time defaults to midnight if the user only set a date.
function joinLocal(date, time) {
  if (!date) return null;
  return `${date}T${time || '00:00'}`;
}

// Plain-English summary of the chosen schedule, echoed back under the picker.
function scheduleEcho(draft) {
  if (draft.mode === 'absolute') {
    if (!draft.fixedDate) return null;
    return formatFixed(joinLocal(draft.fixedDate, draft.fixedTime));
  }
  const n = Math.abs(Number(draft.offsetValue) || 0);
  const unit = draft.offsetUnit || 'days';
  const dir = draft.relativeDir === 'before_event' ? 'before the event' : 'after the event';
  if (n === 0 && draft.relativeDir !== 'before_event') return 'At the event start';
  return `${n} ${unit} ${dir}`;
}

function AutomationEditor({ mode, draft, setDraft, tplOptions, saving, onSave, onCancel, onArchive, busy }) {
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const isAbsolute = draft.mode === 'absolute';
  const echo = scheduleEcho(draft);
  const missingDate = isAbsolute && !draft.fixedDate;

  return (
    <div className="ci-glass" style={{ padding: 18, marginTop: 10, borderColor: 'var(--ci-bdgold)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div className="ci-ins-h" style={{ marginBottom: 0 }}><Zap size={15} /> {mode === 'new' ? 'Create Automation' : 'Edit Automation'}</div>
        <button className="ci-kebab" aria-label="Close editor" onClick={onCancel}><X size={16} /></button>
      </div>

      <div className="ci-lab">Name</div>
      <input className="ci-inp" value={draft.name} placeholder="e.g. RSVP Reminder — Wave 1" onChange={(e) => set({ name: e.target.value })} />

      <div className="ci-create-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
        <div>
          <div className="ci-lab">Trigger — who it applies to</div>
          <select className="ci-inp" value={draft.trigger} onChange={(e) => set({ trigger: e.target.value })}>
            {TRIGGERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>

          <div className="ci-lab">When to send</div>
          <div className="ci-as-modeseg" role="group" aria-label="Scheduling mode">
            <button
              type="button"
              className={`ci-as-mode ${isAbsolute ? 'ci-on' : ''}`}
              aria-pressed={isAbsolute}
              onClick={() => set({ mode: 'absolute' })}
            >
              <CalendarClock size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Date &amp; time
            </button>
            <button
              type="button"
              className={`ci-as-mode ${!isAbsolute ? 'ci-on' : ''}`}
              aria-pressed={!isAbsolute}
              onClick={() => set({ mode: 'relative' })}
            >
              <Clock size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Relative to event
            </button>
          </div>

          {isAbsolute ? (
            <>
              <div className="ci-as-dt">
                <label className="ci-as-dt-field">
                  <span>Send on</span>
                  <input
                    type="date"
                    className="ci-inp ci-as-native"
                    value={draft.fixedDate}
                    onChange={(e) => set({ fixedDate: e.target.value })}
                  />
                </label>
                <label className="ci-as-dt-field">
                  <span>At</span>
                  <input
                    type="time"
                    className="ci-inp ci-as-native"
                    value={draft.fixedTime}
                    onChange={(e) => set({ fixedTime: e.target.value })}
                  />
                </label>
              </div>
              {missingDate && <div className="ci-as-warn">Choose a date to schedule this automation.</div>}
            </>
          ) : (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="number"
                min="0"
                className="ci-inp"
                style={{ maxWidth: 84 }}
                value={draft.offsetValue}
                onChange={(e) => set({ offsetValue: e.target.value })}
              />
              <select className="ci-inp" style={{ maxWidth: 116 }} value={draft.offsetUnit} onChange={(e) => set({ offsetUnit: e.target.value })}>
                {OFFSET_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
              <select className="ci-inp" style={{ maxWidth: 150 }} value={draft.relativeDir} onChange={(e) => set({ relativeDir: e.target.value })}>
                {RELATIVE_DIRS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </div>
          )}

          {echo && (
            <div className="ci-as-echo">
              <CalendarClock size={12} style={{ verticalAlign: -2, marginRight: 5 }} />
              Sends <b>{echo}</b>
            </div>
          )}
        </div>

        <div>
          <div className="ci-lab">Action — Channel</div>
          <div className="ci-seg">
            {[{ k: 'email', L: 'Email', I: Mail }, { k: 'sms', L: 'SMS', I: MessageCircle }].map(({ k, L, I }) => (
              <span key={k} className={`ci-s ${draft.channel === k ? 'ci-on' : ''}`} onClick={() => set({ channel: k, templateId: '' })}>
                <I size={13} style={{ verticalAlign: -2, marginRight: 4 }} />{L}
              </span>
            ))}
          </div>

          <div className="ci-lab">Template to send</div>
          <select className="ci-inp" value={draft.templateId} onChange={(e) => set({ templateId: e.target.value })}>
            <option value="">No template (assign later)</option>
            {tplOptions.map((t) => (
              <option key={String(t.id || t._id)} value={String(t.id || t._id)}>{t.name || 'Untitled template'}</option>
            ))}
          </select>
          {tplOptions.length === 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--ci-txt3)', marginTop: 6 }}>
              No {draft.channel === 'sms' ? 'SMS' : 'email'} templates found. Create one in Templates, then attach it here.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="ci-btn ci-gold ci-sm" onClick={onSave} disabled={saving || busy || missingDate}>
          {saving ? 'Saving…' : (mode === 'new' ? 'Create Automation' : 'Save Changes')}
        </button>
        <button className="ci-btn ci-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        {onArchive && (
          <button className="ci-btn ci-ghost ci-sm" style={{ marginLeft: 'auto', color: 'var(--ci-txt3)' }} onClick={onArchive} disabled={busy} title="Pause this automation">
            <Trash2 size={12} /> Pause
          </button>
        )}
      </div>
    </div>
  );
}
